import React, { useEffect, useState } from 'react';

import { Button, createStyles, Divider, Group, LoadingOverlay, Paper, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import PackageStatusBadge from 'components/status-badges/PackageStatusBadge';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useAthletePackage from 'hooks/athletePackage/useAthletePackage';
import BackHeader from 'layouts/MobileLayout/components/BackHeader';
import { useParams } from 'react-router-dom';
import { CreditDispensation } from 'types';
import { PackageTypeLabelsEnum } from 'types/enums';
import { parseDateTimeToDate } from 'utils/date-functions';

import ModalAthletePackageCancel from './components/AthletePackageCancelModal';

const useStyles = createStyles(theme => ({
  labelGrid: {
    display: 'grid',
    rowGap: theme.spacing.xs,
    columnGap: theme.spacing.md,
    gridTemplateColumns: '2fr 4fr',
    gridTemplateRows: 'auto',
    whiteSpace: 'nowrap',
    [theme.fn.smallerThan('sm')]: {
      fontSize: theme.fontSizes.sm,
    },
  },
  divider: {
    gridColumnStart: '1',
    gridColumnEnd: '3',
  },
  badgeContainer: {
    position: 'absolute',
    top: -10,
    right: 10,
  },
}));

const AthletePackageDetail = (): JSX.Element => {
  const { formatCurrency, dateFormat } = useLocalization();
  const { athletePackageId } = useParams<{ athletePackageId: string }>();
  const { classes } = useStyles();
  const { session } = useAuth();

  const { data: athletePackageData, isError, isLoading, isSuccess } = useAthletePackage(athletePackageId);

  const [creditDispensations, setCreditDispensations] = useState<CreditDispensation[]>([]);
  const [cancelModalOpened, { open: openCancelModal, close: closeCancelModal }] = useDisclosure(false);

  useEffect(() => {
    if (isSuccess && athletePackageData?.data) {
      const sorted = athletePackageData.data.creditDispensations.sort((a, b) =>
        a.dispenseDate < b.dispenseDate ? -1 : 1,
      );
      setCreditDispensations(sorted);
    }
  }, [athletePackageData?.data, isSuccess]);

  const {
    data: nextPackage,
    isSuccess: isNextSuccess,
    isLoading: isNextLoading,
  } = useAthletePackage(athletePackageData?.data?.child?.athletePackageId);

  const getNextPackageStartDate = (): JSX.Element => {
    if (isNextSuccess && nextPackage !== null) {
      return (
        <Text>
          {dayjs(parseDateTimeToDate(nextPackage.data.startDate, session?.gym?.timezone)).format(dateFormat)}
        </Text>
      );
    } else if (isNextLoading) {
      return <LoadingOverlay visible={isNextLoading} />;
    }
    return <Text c='dimmed'>None</Text>;
  };

  const packageType = athletePackageData?.data?.package.type;
  const showsEndDate = packageType === 'TIME_BASED' || packageType === 'TIME_OR_CREDIT_BASED';

  if (isError) {
    return <>Something went wrong</>;
  }
  return (
    <>
      <LoadingOverlay radius='lg' visible={isLoading} />
      <BackHeader
        title={
          athletePackageData?.data?.package.name
            ? `${athletePackageData.data.package.name} Details`
            : 'Package Details'
        }
      />
      {isSuccess && athletePackageData.data && (
        <>
          <Paper shadow='xs' radius='lg' p='sm' mb='sm' pos='relative' w='90%'>
            <div className={classes.labelGrid}>
              <Text color='dimmed' fs='italic' fz='sm' ta='left'>
                Status
              </Text>
              <Group ta='left' fz='sm' ml='auto'>
                <PackageStatusBadge
                  status={athletePackageData.data.status}
                  isCancelEarly={athletePackageData.data.cancellationDate !== null}
                  size='md'
                  w='min-content'
                />
              </Group>
              <Text color='dimmed' fs='italic' fz='sm' ta='left'>
                Type
              </Text>
              <Text ta='left'>{PackageTypeLabelsEnum[athletePackageData?.data.package.type]}</Text>
              <Text color='dimmed' fs='italic' fz='sm' ta='left'>
                Start Date
              </Text>
              <Text ta='left'>
                {dayjs(
                  parseDateTimeToDate(athletePackageData.data.startDate, session?.gym?.timezone),
                ).format(dateFormat)}
              </Text>
              {athletePackageData.data.cancellationDate && (
                <>
                  <Text color='dimmed' fs='italic' fz='sm' ta='left'>
                    New End Date
                  </Text>
                  <Text ta='left'>
                    {dayjs(
                      parseDateTimeToDate(athletePackageData.data.cancellationDate, session?.gym?.timezone),
                    ).format(dateFormat)}
                  </Text>
                </>
              )}

              {showsEndDate && athletePackageData.data.endDate && (
                <>
                  <Text
                    color='dimmed'
                    fs='italic'
                    fz='sm'
                    ta='left'
                    opacity={athletePackageData.data.cancellationDate ? 0.7 : undefined}
                  >
                    {athletePackageData.data.cancellationDate ? 'Original End Date' : 'End Date'}
                  </Text>
                  <Text
                    ta='left'
                    color={athletePackageData.data.cancellationDate ? 'dimmed' : undefined}
                    opacity={athletePackageData.data.cancellationDate ? 0.7 : undefined}
                  >
                    {dayjs(
                      parseDateTimeToDate(athletePackageData.data.endDate, session?.gym?.timezone),
                    ).format(dateFormat)}
                  </Text>
                </>
              )}

              <Text color='dimmed' fs='italic' fz='sm' ta='left'>
                Upcoming Package
              </Text>
              <Text ta='left'>{getNextPackageStartDate()}</Text>
              <Text color='dimmed' fs='italic' fz='sm' ta='left'>
                Auto Renew
              </Text>
              <Text ta='left'>{athletePackageData.data.autoRenew ? 'On' : 'Off'}</Text>
              {creditDispensations.length === 0 ? (
                <>
                  <Divider mx='-md' className={classes.divider} />
                  <Text size='sm' color='dimmed' fw={600}>
                    No active credits
                  </Text>
                </>
              ) : (
                creditDispensations.map(creditDispensation => (
                  <>
                    <Divider mx='-md' className={classes.divider} />
                    <Text color='dimmed' fs='italic' fz='sm' ta='left'>
                      Event Type
                    </Text>
                    <Text ta='left'>{creditDispensation.name}</Text>
                    <Text color='dimmed' fs='italic' fz='sm' ta='left'>
                      Attendance
                    </Text>
                    <Text ta='left'>
                      {creditDispensation.credits ? creditDispensation.credits : 'Unlimited'} Sessions
                    </Text>
                  </>
                ))
              )}
            </div>
          </Paper>

          <Paper shadow='xs' radius='lg' p='sm' mb='sm' pos='relative' w='90%'>
            <div className={classes.labelGrid}>
              <Text color='dimmed' fs='italic' fz='sm' ta='left'>
                Billing Plan
              </Text>
              <Text ta='left'>{athletePackageData.data.billingPlan.name}</Text>
              <Text color='dimmed' fs='italic' fz='sm' ta='left'>
                Total Price
              </Text>
              <Text ta='left'>{formatCurrency(athletePackageData.data.billingPlan.totalPrice / 100)}</Text>
              <Text color='dimmed' fs='italic' fz='sm' ta='left'>
                Installments
              </Text>
              <Text ta='left'>{athletePackageData.data.billingPlan.numberOfInstallments}</Text>
              <Text color='dimmed' fs='italic' fz='sm' ta='left'>
                Auto Pay
              </Text>
              <Text ta='left'>{athletePackageData.data.autoPay ? 'On' : 'Off'}</Text>
            </div>
          </Paper>
          {(athletePackageData.data.status === 'ACTIVE' ||
            athletePackageData.data.status === 'HOLD' ||
            athletePackageData.data.status === 'UPCOMING') &&
            athletePackageData.data.cancellationDate === null &&
            athletePackageData.data.package.clickToCancelEnabled === true && (
              <Group position='center' px='lg'>
                <Button size='md' radius='xl' fullWidth onClick={openCancelModal} color='red'>
                  Cancel Package
                </Button>
              </Group>
            )}

          <ModalAthletePackageCancel
            opened={cancelModalOpened}
            handleClose={closeCancelModal}
            athletePackageId={athletePackageData.data.athletePackageId}
          />
        </>
      )}
    </>
  );
};

export default AthletePackageDetail;
